/* eslint-disable no-prototype-builtins */
/* eslint-disable eqeqeq */
/* eslint-disable no-nested-ternary */
import React, { useContext, useEffect, useRef, useState } from "react";

import { Checkbox, TableRow, Tooltip } from "@mui/material";
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  IndeterminateCheckBox as IndeterminateCheckBoxIcon,
} from "@mui/icons-material";

import TableCell from "../components/TableCell";
import Context from "../context-index";
import { Column, Constants, RowActions, RowActionsExtra } from "../export";
import GridActions, { FiEdit } from "../GridActions";
import { getBrandCustom } from "../utils/getCustomColor";
import { CellActionContainer } from "./styles";
import { colors } from "@zapalacorp/tokens";

interface Props {
  length: number;
  index: number;
  row: any;
  columns: Column[];
  showSelection: boolean;
  selectedList: number[];
  handleSelectRow: Function;
  rowActions: RowActions | undefined;
  rowActionsExtra?: RowActionsExtra[];
  positionActions: "left" | "right";
  rowId: number;
  rowStyle?: React.CSSProperties;
  isInside?: boolean;
  enableGetRowSize?: boolean;
  pinnedTable?: boolean;
  endScrolled: boolean;
  stickyActions: boolean;
  gridCellPadding?: string;
}

type DataProps = Props;

const GridData = ({
  length,
  row,
  rowId,
  columns,
  showSelection,
  selectedList,
  handleSelectRow,
  rowActions,
  rowActionsExtra,
  positionActions,
  index,
  rowStyle,
  isInside,
  enableGetRowSize,
  pinnedTable,
  endScrolled,
  stickyActions,
  gridCellPadding,
}: DataProps) => {
  const { selectedColumns, handleSetRowSizes, rowSizes } = useContext(Context);

  const renderCheckbox = () => {
    if (showSelection && selectedColumns.length >= 1 && !String(rowId).includes("_colspan")) {
      return (
        <TableCell data-cy="checkbox" width="50px" padding="none" className="td-checkbox">
          <Checkbox
            color="default"
            icon={<CheckBoxOutlineBlankIcon htmlColor="#D6DAE1" />}
            checkedIcon={
              <CheckBoxIcon htmlColor={getBrandCustom("primaryColor", colors.brandBlue500)} />
            }
            indeterminateIcon={
              <IndeterminateCheckBoxIcon
                htmlColor={getBrandCustom("primaryColor", colors.brandBlue500)}
              />
            }
            checked={!!selectedList?.find((value) => value === rowId)}
            onChange={(e) => handleSelectRow(rowId, e.target.checked)}
          />
        </TableCell>
      );
    }
    if (!showSelection && isInside && !String(rowId).includes("_colspan")) {
      return (
        <TableCell
          data-cy="checkbox"
          width="50px"
          padding="none"
          className="td-checkbox"
        ></TableCell>
      );
    }
  };

  const rowRef = useRef<HTMLTableRowElement>(null);

  const getRowHeight = () => {
    const element = rowRef?.current;

    if (element && (rowSizes?.[index] !== element?.offsetHeight || !rowSizes))
      handleSetRowSizes(index, element?.offsetHeight);
  };

  useEffect(() => {
    if (enableGetRowSize) {
      getRowHeight();
      window.addEventListener("resize", getRowHeight);
    }
  }, [enableGetRowSize, rowRef?.current?.offsetHeight]);

  return (
    <TableRow
      ref={rowRef}
      style={
        rowStyle || {
          backgroundColor: index % 2 === 1 || length === 1 ? "white" : "#f2f4f7",
          ...(pinnedTable && rowSizes ? { height: rowSizes[index] } : {}),
        }
      }
      data-cy="table-row"
    >
      {renderCheckbox()}
      {positionActions == "left" &&
        (rowActions || rowActionsExtra) &&
        selectedColumns.length >= 1 &&
        !String(rowId).includes("_colspan") && (
          <GridActions
            index={index}
            row={row}
            rowId={rowId}
            actions={rowActions}
            rowActionsExtra={rowActionsExtra}
            endScrolled={endScrolled}
            isOneElement={length === 1}
          />
        )}

      {String(rowId).includes("_colspan") ? (
        <TableCell
          padding={"none"}
          className="td-colspan"
          data-cy="table-cell"
          sx={{
            padding: gridCellPadding ?? "5px 8px",
            position: "relative",
          }}
          colSpan={columns.length + 1}
        >
          {row.hasOwnProperty("render") ? row.render : <span>&nbsp;</span>}
        </TableCell>
      ) : (
        columns.map(
          (
            {
              name,
              formatText,
              formatComponent,
              limitRowsToEllipsis,
              className,
              style,
              handleEdit,
              stickyRight,
            },
            columnIndex
          ) => {
            const [hoverStatus, setHover] = useState(false);
            const textElementRef = useRef<HTMLSpanElement>(null);
            const [showEdit, setShowEdit] = useState<boolean>(false);

            const haveEllipsis = (element: HTMLElement) => {
              return !(
                element.offsetWidth === element.scrollWidth &&
                element.offsetHeight === element.scrollHeight
              );
            };

            const compareSize = () => {
              const element = textElementRef?.current;

              if (
                element &&
                element.offsetWidth &&
                element.scrollWidth &&
                element.offsetHeight &&
                element.scrollHeight
              ) {
                setHover(haveEllipsis(element));
              }
            };

            const textWithTooltip = (text: string, limitRows?: number) => {
              return (
                <Tooltip title={text} disableHoverListener={!hoverStatus}>
                  <span
                    className={limitRows ? "cell-text-ellipsis" : undefined}
                    style={{
                      WebkitLineClamp: limitRows ?? "none",
                    }}
                    ref={textElementRef}
                  >
                    {text}
                  </span>
                </Tooltip>
              );
            };

            const renderCellInfo = () => {
              if (formatText) {
                if (typeof formatText(row[name], isInside) === "string") {
                  return textWithTooltip(formatText(row[name], isInside), limitRowsToEllipsis);
                }

                return formatText(row[name], isInside);
              }

              return textWithTooltip(row[name], limitRowsToEllipsis ?? 3);
            };

            const renderEdit = (key: number) => {
              return (
                <Tooltip key={key} data-cy="action-edit" title="Edit" placement="top">
                  <div>
                    <FiEdit
                      color={Constants.defaultColorIcon}
                      size={Constants.defaultRowActionSize}
                      onClick={() => handleEdit?.(rowId, row, name)}
                    />
                  </div>
                </Tooltip>
              );
            };

            useEffect(() => {
              compareSize();
              window.addEventListener("resize", compareSize);
            }, []);

            useEffect(
              () => () => {
                window.removeEventListener("resize", compareSize);
              },
              []
            );

            const handleMouse = (isEnter: boolean) => {
              if (!handleEdit) return;
              setShowEdit(isEnter);
            };
            return (
              <TableCell
                key={columnIndex}
                index={index}
                data-cy={`table-cell-${name.toLowerCase()}`}
                style={{
                  display: selectedColumns.some((column) => column === columnIndex) ? "" : "none",
                  padding: gridCellPadding ?? "5px 8px",
                  position: "relative",
                  ...style,
                }}
                padding="none"
                className={className}
                onMouseEnter={() => handleMouse(true)}
                onMouseLeave={() => handleMouse(false)}
                sticky={!!stickyRight}
                right={stickyRight}
                endScrolled={endScrolled}
              >
                {selectedColumns.length > 0
                  ? selectedColumns.some((column) => column === columnIndex) ||
                    selectedColumns.length === columns.length
                    ? formatComponent
                      ? formatComponent(row[name], row, isInside)
                      : renderCellInfo()
                    : null
                  : null}

                {showEdit && handleEdit ? (
                  <CellActionContainer
                    backgroundColor={index % 2 === 1 || length === 1 ? "white" : "#f2f4f7"}
                  >
                    {renderEdit(columnIndex)}
                  </CellActionContainer>
                ) : null}
              </TableCell>
            );
          }
        )
      )}

      {positionActions == "right" &&
        (rowActions || rowActionsExtra) &&
        selectedColumns.length >= 1 &&
        !String(rowId).includes("_colspan") && (
          <GridActions
            index={index}
            row={row}
            rowId={rowId}
            actions={rowActions}
            rowActionsExtra={rowActionsExtra}
            endScrolled={endScrolled}
            stickyActions={stickyActions}
            isOneElement={length === 1}
          />
        )}
    </TableRow>
  );
};

export default GridData;
